/**
 * docx.js — DOCX file manipulation utilities
 *
 *
 * A .docx file is a ZIP archive. Inside it, `word/document.xml`
 * holds the actual document content as XML with this structure:
 *
 *   <w:p>          → paragraph  (like a <p> in HTML)
 *     <w:r>        → "run"      (a span of text sharing the same formatting)
 *       <w:t>      → text node  (the actual characters)
 *
 * The tricky part: a single visible word can be split across
 * MULTIPLE <w:t> nodes if formatting changes mid-word.
 * For example, "Hello" in bold-then-italic might be stored as:
 *
 *   <w:r><w:rPr><w:b/></w:rPr><w:t>Hel</w:t></w:r>
 *   <w:r><w:rPr><w:i/></w:rPr><w:t>lo</w:t></w:r>
 *
 * ═══════════════════════════════════════════════════════════
 *  PARAGRAPH MODEL (the core data structure)
 * ═══════════════════════════════════════════════════════════
 *
 * To handle cross-node edits, we build a "paragraph model" that
 * flattens all <w:t> nodes into one continuous string per paragraph:
 *
 *   paragraphs[i].text            = concatenated text from all <w:t> nodes
 *   textNodesByParagraph[i]       = [{node, value, isHyperlink, isField}]
 *   offsetMap[i]                  = [{start, end, nodeIndex}]
 *      ↑ maps character offsets back to specific <w:t> DOM nodes
 *
 * This lets Gemini issues (which reference character offsets) map
 * cleanly to the underlying XML nodes for safe replacement.
 *
 * ═══════════════════════════════════════════════════════════
 *  PARAGRAPH ID INJECTION
 * ═══════════════════════════════════════════════════════════
 *
 * injectParagraphIds() stamps each <w:p> with a w14:paraId attribute
 * before rendering. docx-preview preserves these as data-para-id on
 * rendered <p> elements, giving us deterministic paragraph mapping
 * between the XML model and the visible preview DOM — no fragile
 * text-matching heuristics needed.
 */
import JSZip from 'jszip';
import { DOMParser, XMLSerializer } from '@xmldom/xmldom';

/** Path to the main document content inside the DOCX zip archive. */
export const DOC_XML_PATH = 'word/document.xml';

/* ══════════════════════════════════════════════════════════════
 *  Text helpers
 * ══════════════════════════════════════════════════════════════ */

/**
 * Collapse all whitespace (tabs, newlines, multiple spaces) into
 * single spaces and trim edges. Used when comparing paragraph text
 * between the XML model and the rendered preview DOM, where
 * whitespace handling may differ.
 */
export function normalizeText(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Truncate text for display in error messages and UI tooltips.
 * Adds "…" when truncated so users know text was cut off.
 *
 * @param {string} text       - Raw text to truncate
 * @param {number} maxLength  - Maximum characters before truncation (default 80)
 */
export function previewShort(text, maxLength = 80) {
  const cleaned = normalizeText(text);
  return cleaned.length <= maxLength ? cleaned : `${cleaned.slice(0, maxLength)}…`;
}

/* ══════════════════════════════════════════════════════════════
 *  File I/O — reading, zipping, and downloading DOCX files
 * ══════════════════════════════════════════════════════════════ */

/** Read a browser File object into an ArrayBuffer for JSZip processing. */
export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

/** Unzip a DOCX ArrayBuffer into a mutable JSZip instance. */
export async function unzipDocx(arrayBuffer) {
  return JSZip.loadAsync(arrayBuffer);
}

/**
 * Read a text file from inside the DOCX zip archive.
 * Used primarily to extract word/document.xml for parsing.
 */
export async function getTextFromZip(zip, filePath) {
  const zipEntry = zip.file(filePath);
  if (!zipEntry) throw new Error(`Missing ${filePath} in DOCX`);
  return zipEntry.async('text');
}

/**
 * Write text content into a file inside the DOCX zip archive.
 * Used to update word/document.xml after applying text replacements.
 */
export function setTextInZip(zip, filePath, textContent) {
  zip.file(filePath, textContent);
}

/** Convert the entire zip archive to an ArrayBuffer (for docx-preview rendering). */
export async function zipToArrayBuffer(zip) {
  return zip.generateAsync({ type: 'arraybuffer' });
}

/**
 * Download the current zip as a .docx file to the user's computer.
 * Creates a temporary <a> link, clicks it, then cleans up the blob URL.
 */
export async function downloadZipAsDocx(zip, filename) {
  const blob = await zip.generateAsync({ type: 'blob' });
  const downloadLink = document.createElement('a');
  downloadLink.href = URL.createObjectURL(blob);
  downloadLink.download = filename;
  downloadLink.click();
  URL.revokeObjectURL(downloadLink.href);
}

/* ══════════════════════════════════════════════════════════════
 *  XML helpers
 * ══════════════════════════════════════════════════════════════ */

/**
 * Check if a DOM node is nested inside a given XML tag.
 * Used to detect whether a <w:t> text node lives inside a
 * hyperlink (<w:hyperlink>) or field — these are "protected"
 * and should not be edited by style-guide replacements.
 */
function isNestedUnderTag(node, tagName) {
  let current = node;
  while (current) {
    if (current.nodeType === 1) {
      const nodeName = current.nodeName || '';
      if (nodeName === tagName || nodeName.endsWith(':' + tagName)) return true;
    }
    current = current.parentNode;
  }
  return false;
}

/* ══════════════════════════════════════════════════════════════
 *  Parse document.xml → paragraph model
 *
 *  This is the entry point for turning raw DOCX XML into the
 *  structured data used by every other module:
 *    - gemini.js uses paragraphs[] for offset resolution
 *    - highlight.js uses paragraphs[] for DOM mapping
 *    - issue.js uses paragraphs[] for anchor rebuilding
 *    - StyleGuideChecker uses textNodesByParagraph + offsetMap
 *      for safe XML replacement via applyReplacementInParagraph()
 * ══════════════════════════════════════════════════════════════ */

/**
 * Parse the document XML into a structured paragraph model.
 *
 * @param {string} documentXml - Raw XML string from word/document.xml
 * @returns {{
 *   xmlDoc:               Document,
 *   paragraphs:           Array<{paragraphId: number, text: string}>,
 *   textNodesByParagraph:  Map<number, Array>,  — paragraphId → [{node, value, isHyperlink, isField}]
 *   offsetMap:            Map<number, Array>,  — paragraphId → [{start, end, nodeIndex}]
 * }}
 *
 */
export function parseDocXmlToParagraphs(documentXml) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(documentXml, 'text/xml');

  const paragraphs = [];
  /** Maps paragraphId → array of text node metadata objects. */
  const textNodesByParagraph = new Map();
  /** Maps paragraphId → array of {start, end, nodeIndex} character offset ranges. */
  const offsetMap = new Map();

  const paragraphNodes = xmlDoc.getElementsByTagName('w:p');

  for (let paragraphIndex = 0; paragraphIndex < paragraphNodes.length; paragraphIndex++) {
    const paragraphNode = paragraphNodes.item(paragraphIndex);
    const textElements = paragraphNode.getElementsByTagName('w:t');

    /*
     * Step 1: Build metadata for each <w:t> text node in this paragraph.
     * We record whether each node is inside a hyperlink or field so that
     * applyReplacementInParagraph() can refuse to edit protected content.
     */
    const textNodesMetadata = [];
    for (let nodeIdx = 0; nodeIdx < textElements.length; nodeIdx++) {
      const textElement = textElements.item(nodeIdx);
      textNodesMetadata.push({
        node: textElement,
        value: textElement?.textContent || '',
        isHyperlink: isNestedUnderTag(textElement, 'w:hyperlink'),
        isField:
          isNestedUnderTag(textElement, 'w:fldSimple') ||
          isNestedUnderTag(textElement, 'w:instrText') ||
          isNestedUnderTag(textElement, 'w:fldChar'),
      });
    }

    /*
     * Step 2: Build the offset map — for each <w:t> node, record where its
     * characters sit in the flattened paragraph string. This is how we
     * translate a character offset (e.g., "replace chars 10-15") back to
     * the correct XML <w:t> node(s) to modify.
     *
     * Example for a paragraph with 3 text nodes ["Hello", " ", "World"]:
     *   flattenedText = "Hello World"
     *   offsetRanges  = [
     *     { start: 0,  end: 5,  nodeIndex: 0 },   // "Hello"
     *     { start: 5,  end: 6,  nodeIndex: 1 },   // " "
     *     { start: 6,  end: 11, nodeIndex: 2 },   // "World"
     *   ]
     */
    let flattenedText = '';
    const offsetRanges = [];
    for (let nodeIdx = 0; nodeIdx < textNodesMetadata.length; nodeIdx++) {
      const charStart = flattenedText.length;
      flattenedText += textNodesMetadata[nodeIdx].value;
      offsetRanges.push({
        start: charStart,
        end: flattenedText.length,
        nodeIndex: nodeIdx,
      });
    }

    paragraphs.push({ paragraphId: paragraphIndex, text: flattenedText });
    textNodesByParagraph.set(paragraphIndex, textNodesMetadata);
    offsetMap.set(paragraphIndex, offsetRanges);
  }

  return { xmlDoc, paragraphs, textNodesByParagraph, offsetMap };
}

/**
 * Inject w14:paraId attributes on every <w:p> element for deterministic
 * paragraph mapping between XML and the rendered preview DOM.
 *
 * WHY: docx-preview renders each <w:p> as a <p> in the browser.
 * By stamping each <w:p> with a unique paraId, we get a reliable
 * data-para-id attribute on rendered <p> elements. This gives
 * highlight.js a way to map "paragraph #7 in the XML" → "the correct
 * <p> element in the preview DOM" without brittle text-matching.
 *
 * Call on the XML string BEFORE passing to renderAsync().
 * Returns the modified XML string.
 */
export function injectParagraphIds(documentXml) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(documentXml, 'text/xml');
  const serializer = new XMLSerializer();

  const paragraphNodes = xmlDoc.getElementsByTagName('w:p');
  for (let index = 0; index < paragraphNodes.length; index++) {
    // Pad to 8 digits for consistent formatting (e.g., "00000007").
    paragraphNodes.item(index).setAttribute('w14:paraId', String(index).padStart(8, '0'));
  }

  return serializer.serializeToString(xmlDoc);
}

/* ══════════════════════════════════════════════════════════════
 *  Apply replacement within a paragraph
 *
 *  This is the core edit operation used by accept, revert, acceptAll,
 *  and undoAll. It handles:
 *
 *   - Replacements spanning multiple <w:t> nodes
 *     (puts all new text in the first node, clears the rest)
 *
 *   - Common prefix/suffix optimization
 *     (minimizes XML changes by narrowing the edit range)
 *
 *   - Protection against editing hyperlinks and fields
 *
 *   - Zero-width insertions (startOffset === endOffset)
 *     This edge case happens when prefix/suffix optimization reduces
 *     a revert to a pure insertion. Example: reverting ", and" → " and"
 *     becomes inserting "," at a <w:t> boundary.
 * ══════════════════════════════════════════════════════════════ */

/**
 * Replace text at [startOffset, endOffset) in a paragraph with replacementText.
 *
 * @param {Object}   params
 * @param {Document} params.xmlDoc                 - Mutable parsed XML DOM
 * @param {Map}      params.textNodesByParagraph    - paragraphId → text node metadata
 * @param {Map}      params.offsetMap               - paragraphId → offset ranges
 * @param {number}   params.paragraphId             - Which paragraph to edit
 * @param {number}   params.startOffset             - Inclusive start character offset
 * @param {number}   params.endOffset               - Exclusive end character offset
 * @param {string}   params.replacementText         - Text to insert in place of original
 * @returns {string} Serialized XML string after replacement
 */
export function applyReplacementInParagraph({
  xmlDoc,
  textNodesByParagraph,
  offsetMap,
  paragraphId,
  startOffset,
  endOffset,
  replacementText,
}) {
  if (startOffset < 0 || endOffset < startOffset) throw new Error('Invalid range');

  const textNodesMetadata = textNodesByParagraph.get(paragraphId);
  const offsetRanges = offsetMap.get(paragraphId);
  if (!textNodesMetadata || !offsetRanges) throw new Error('Paragraph not found');

  const serializer = new XMLSerializer();

  /*
   * Helper: find which <w:t> offset ranges overlap a character span.
   * Returns array of indexes into offsetRanges[].
   * Only used for non-zero-width ranges (zero-width handled separately below).
   */
  const findOverlappingRanges = (rangeStart, rangeEnd) => {
    const overlappingIndexes = [];
    for (let i = 0; i < offsetRanges.length; i++) {
      if (offsetRanges[i].end <= rangeStart) continue; // entirely before the edit
      if (offsetRanges[i].start >= rangeEnd) break; // entirely after the edit
      overlappingIndexes.push(i);
    }
    return overlappingIndexes;
  };

  /* Helper: check if any overlapping nodes are hyperlinks or fields. */
  const hasProtectedNodes = (rangeIndexes) =>
    rangeIndexes.some((i) => textNodesMetadata[i]?.isHyperlink || textNodesMetadata[i]?.isField);

  /*
   * ── STEP 1: Common prefix/suffix optimization ──
   *
   * If the replacement shares leading/trailing characters with the original,
   * narrow the edit range to just the actual difference. This minimizes the
   * number of XML nodes we need to touch.
   *
   * Example: replacing "the University" with "The University"
   *   originalText    = "the University"
   *   replacementText = "The University"
   *   sharedPrefix = "" (differs at char 0: 't' vs 'T')
   *   sharedSuffix = "he University" (13 chars)
   *   → narrowed to: replace "t" with "T" at offset 0..1
   */
  const paragraphText = offsetRanges.reduce(
    (accumulated, range) => accumulated + (textNodesMetadata[range.nodeIndex]?.value || ''),
    '',
  );
  const originalText = (paragraphText || '').slice(startOffset, endOffset);

  // No-op: original and replacement are identical.
  if (originalText === replacementText) return serializer.serializeToString(xmlDoc);

  const sharedPrefixLength = commonPrefixLen(originalText, replacementText);
  const sharedSuffixLength = commonSuffixLen(originalText, replacementText, sharedPrefixLength);

  // If there IS a shared prefix/suffix, recurse with the narrowed range.
  if (
    (sharedPrefixLength > 0 || sharedSuffixLength > 0) &&
    sharedPrefixLength + sharedSuffixLength < Math.max(originalText.length, replacementText.length)
  ) {
    return applyReplacementInParagraph({
      xmlDoc,
      textNodesByParagraph,
      offsetMap,
      paragraphId,
      startOffset: startOffset + sharedPrefixLength,
      endOffset: endOffset - sharedSuffixLength,
      replacementText: replacementText.slice(
        sharedPrefixLength,
        replacementText.length - sharedSuffixLength,
      ),
    });
  }

  /*
   * ── STEP 2: Zero-width insertion (startOffset === endOffset) ──
   *
   * Must be handled BEFORE findOverlappingRanges(), because that function
   * uses strict overlap logic (start < end) which returns empty for point
   * queries that land on a <w:t> node boundary.
   */
  if (startOffset === endOffset) {
    // Find the <w:t> node that contains or touches this insertion point.
    // Uses inclusive bounds (<=, >=) to handle boundary points where
    // one node ends and the next begins at the same offset.
    let targetRangeIndex = -1;
    for (let i = 0; i < offsetRanges.length; i++) {
      if (offsetRanges[i].start <= startOffset && offsetRanges[i].end >= startOffset) {
        targetRangeIndex = i;
        break;
      }
    }

    if (targetRangeIndex === -1) {
      throw new Error(
        `No <w:t> node found for insertion at offset ${startOffset} in paragraph ${paragraphId}.`,
      );
    }

    if (
      textNodesMetadata[targetRangeIndex]?.isHyperlink ||
      textNodesMetadata[targetRangeIndex]?.isField
    ) {
      throw new Error('Cannot edit inside hyperlink/field text.');
    }

    const targetRange = offsetRanges[targetRangeIndex];
    const targetNode = textNodesMetadata[targetRangeIndex];
    const currentValue = targetNode.value || '';
    // localOffset is the position within this specific <w:t> node's text.
    const localOffset = Math.max(0, startOffset - targetRange.start);
    const updatedValue =
      currentValue.slice(0, localOffset) + replacementText + currentValue.slice(localOffset);

    targetNode.node.textContent = updatedValue;
    targetNode.value = updatedValue;

    return serializer.serializeToString(xmlDoc);
  }

  /*
   * ── STEP 3: Non-zero-width replacement ──
   *
   * Strategy: put ALL replacement text into the FIRST overlapping <w:t>
   * node, then clear all subsequent overlapping nodes to empty strings.
   * This preserves the first node's formatting for the new text.
   */
  const overlappingIndexes = findOverlappingRanges(startOffset, endOffset);
  if (overlappingIndexes.length === 0) {
    throw new Error('No <w:t> nodes overlapped for this range.');
  }
  if (hasProtectedNodes(overlappingIndexes)) {
    throw new Error('Cannot edit inside hyperlink/field text.');
  }

  const firstOverlapIdx = overlappingIndexes[0];
  const lastOverlapIdx = overlappingIndexes[overlappingIndexes.length - 1];

  const firstNodeValue = textNodesMetadata[firstOverlapIdx].value || '';
  const lastNodeValue = textNodesMetadata[lastOverlapIdx].value || '';

  // Characters in the first node BEFORE the edit range (preserved as-is).
  const preservedPrefix = firstNodeValue.slice(
    0,
    Math.max(0, startOffset - offsetRanges[firstOverlapIdx].start),
  );
  // Characters in the last node AFTER the edit range (preserved as-is).
  const preservedSuffix = lastNodeValue.slice(
    Math.max(0, endOffset - offsetRanges[lastOverlapIdx].start),
  );

  // Write combined text into first node.
  const combinedValue = preservedPrefix + replacementText + preservedSuffix;
  textNodesMetadata[firstOverlapIdx].node.textContent = combinedValue;
  textNodesMetadata[firstOverlapIdx].value = combinedValue;

  // Clear all other overlapping nodes (their text is now in the first node).
  for (let k = 1; k < overlappingIndexes.length; k++) {
    textNodesMetadata[overlappingIndexes[k]].node.textContent = '';
    textNodesMetadata[overlappingIndexes[k]].value = '';
  }

  return serializer.serializeToString(xmlDoc);
}

/* ── String comparison helpers for prefix/suffix optimization ── */

/** Count how many leading characters two strings share. */
function commonPrefixLen(stringA, stringB) {
  const maxLength = Math.min(stringA.length, stringB.length);
  let shared = 0;
  for (; shared < maxLength; shared++) {
    if (stringA[shared] !== stringB[shared]) break;
  }
  return shared;
}

/**
 * Count how many trailing characters two strings share,
 * excluding any characters already counted as common prefix.
 */
function commonSuffixLen(stringA, stringB, prefixLength) {
  const maxLength = Math.min(stringA.length - prefixLength, stringB.length - prefixLength);
  let shared = 0;
  for (; shared < maxLength; shared++) {
    if (stringA[stringA.length - 1 - shared] !== stringB[stringB.length - 1 - shared]) break;
  }
  return shared;
}
